import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { PageSpinner, EmptyState, Modal } from "../components/ui.js";
import { IconTarget } from "../components/icons.js";

interface Goal {
  id: string;
  goalType: string;
  title: string;
  targetValue: number | null;
  unit: string | null;
  period: "week" | "total";
  status: "active" | "achieved" | "archived";
}
interface GoalProgress {
  goal: Goal;
  current: number | null;
  target: number | null;
  unit: string | null;
  ratio: number | null;
  label: string;
  met: boolean;
}

const TYPES: { key: string; label: string; unit: string; placeholder: string; hint: string }[] = [
  { key: "goal_weight", label: "Goal weight", unit: "lb", placeholder: "220", hint: "Your provisional target weight. Progress tracks your smoothed trend." },
  { key: "weekly_workouts", label: "Workouts / week", unit: "", placeholder: "3", hint: "How many training sessions you're aiming for each week." },
  { key: "weekly_walk_minutes", label: "Walking / week", unit: "min", placeholder: "150", hint: "Total walking minutes per week." },
  { key: "nutrition_consistency", label: "Nutrition logging", unit: "days", placeholder: "6", hint: "Days per week you want to log your food." },
  { key: "custom", label: "Custom habit", unit: "", placeholder: "", hint: "A habit you define and check off yourself." }
];

export function GoalsPage() {
  const [goals, setGoals] = useState<GoalProgress[] | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try { setGoals((await api.get<{ goals: GoalProgress[] }>("/api/goals")).goals); }
    catch { setGoals([]); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const active = (goals ?? []).filter((g) => g.goal.status === "active");
  const achieved = (goals ?? []).filter((g) => g.goal.status === "achieved");

  return (
    <div>
      <div className="page-header">
        <div className="row-between">
          <div>
            <h1>Goals</h1>
            <p className="subtitle">A few that matter — progress without the gamification.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setAdding(true)}>New goal</button>
        </div>
      </div>

      {!goals ? <PageSpinner /> : goals.length === 0 ? (
        <EmptyState icon={<IconTarget />} title="Set your first goal" action={<button className="btn btn-sm mt-4" onClick={() => setAdding(true)}>New goal</button>}>
          A goal weight, weekly workouts, walking minutes, or a habit to keep. ClovaForm tracks progress from what you actually log.
        </EmptyState>
      ) : (
        <div className="stack">
          {active.map((g) => <GoalCard key={g.goal.id} g={g} onChange={load} />)}
          {achieved.length > 0 && (
            <>
              <div className="nav-section-label" style={{ padding: "var(--space-4) 0 0" }}>Achieved</div>
              {achieved.map((g) => <GoalCard key={g.goal.id} g={g} onChange={load} />)}
            </>
          )}
        </div>
      )}

      {adding && <AddGoalModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); void load(); }} />}
    </div>
  );
}

function GoalCard({ g, onChange }: { g: GoalProgress; onChange: () => void | Promise<void> }) {
  const pct = g.ratio !== null ? Math.round(g.ratio * 100) : null;
  const achieved = g.goal.status === "achieved";
  return (
    <div className="card">
      <div className="row-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{g.goal.title}</div>
          <div className="muted text-sm">{g.label}</div>
        </div>
        {(g.met || achieved) && <span className="badge badge-success">Met</span>}
      </div>
      {pct !== null && (
        <div className="progress-track mt-3" aria-label={`${pct}% toward goal`}>
          <div className="progress-fill" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      )}
      <div className="row mt-3" style={{ gap: "var(--space-2)" }}>
        {!achieved && g.goal.goalType === "custom" && (
          <button className="btn btn-sm" onClick={async () => { await api.post(`/api/goals/${g.goal.id}/status`, { status: "achieved" }); await onChange(); }}>Mark done</button>
        )}
        {!achieved ? (
          <button className="btn btn-sm btn-ghost" onClick={async () => { await api.post(`/api/goals/${g.goal.id}/status`, { status: "archived" }); await onChange(); }}>Archive</button>
        ) : (
          <button className="btn btn-sm btn-ghost" onClick={async () => { await api.post(`/api/goals/${g.goal.id}/status`, { status: "active" }); await onChange(); }}>Reopen</button>
        )}
        <button className="btn btn-sm btn-ghost" onClick={async () => { if (window.confirm("Delete this goal?")) { await api.del(`/api/goals/${g.goal.id}`); await onChange(); } }}>Delete</button>
      </div>
    </div>
  );
}

function AddGoalModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [type, setType] = useState(TYPES[0]!);
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const t = title.trim() || defaultTitle(type.key, target);
    if (!t) { setError("Give your goal a title."); return; }
    if (type.key !== "custom" && !target.trim()) { setError("Enter a target."); return; }
    setSaving(true); setError(null);
    try {
      await api.post("/api/goals", {
        goalType: type.key,
        title: t,
        targetValue: type.key === "custom" ? null : Number(target),
        unit: type.unit || null,
        period: type.key.startsWith("weekly") || type.key === "nutrition_consistency" ? "week" : "total"
      });
      onSaved();
    } catch { setError("Couldn't save. Try again."); setSaving(false); }
  }

  return (
    <Modal title="New goal" onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="field">
        <label>Type</label>
        <div className="chip-row">
          {TYPES.map((t) => (
            <button key={t.key} type="button" className={`chip${type.key === t.key ? " active" : ""}`} onClick={() => setType(t)}>{t.label}</button>
          ))}
        </div>
        <span className="hint">{type.hint}</span>
      </div>
      {type.key !== "custom" && (
        <div className="field">
          <label htmlFor="g-target">Target {type.unit && <span className="dim">({type.unit})</span>}</label>
          <input id="g-target" className="input" inputMode="numeric" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={type.placeholder} />
        </div>
      )}
      <div className="field">
        <label htmlFor="g-title">Title <span className="dim">(optional)</span></label>
        <input id="g-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={defaultTitle(type.key, target) || "My goal"} maxLength={120} />
      </div>
      <div className="row mt-4">
        <button className="btn btn-primary grow" onClick={save} disabled={saving}>{saving ? "Saving…" : "Add goal"}</button>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
      </div>
    </Modal>
  );
}

function defaultTitle(type: string, target: string): string {
  switch (type) {
    case "goal_weight": return target ? `Reach ${target} lb` : "Goal weight";
    case "weekly_workouts": return target ? `${target} workouts a week` : "Weekly workouts";
    case "weekly_walk_minutes": return target ? `${target} walking minutes a week` : "Weekly walking";
    case "nutrition_consistency": return target ? `Log food ${target} days a week` : "Nutrition consistency";
    default: return "";
  }
}
